import {
  buildCognitoryQuestionAnswerEvaluationSettingsPath,
  resolveCognitoryApiBaseUrl,
} from "./cognitoryApiBase";
import { resolveCognitoryBearerToken } from "./cognitoryBearerToken";
import {
  rebuildCognitoryQuestionAnswerEvaluationSettingsPayload,
  type CognitoryQuestionAnswerEvaluationSettingsView,
} from "./cognitoryQuestionAnswerEvaluationSettingsRebuild";

export type CognitoryQuestionAnswerEvaluationSettingsClientResult =
  | {
      kind: "success";
      requestId: string;
      cognitoryId: string;
      settings: CognitoryQuestionAnswerEvaluationSettingsView;
    }
  | {
      kind:
        | "unauthenticated"
        | "forbidden"
        | "invalid_request"
        | "temporary_failure"
        | "malformed_success";
      requestId: string | null;
    };

function readJsonRequestId(body: unknown): string | null {
  if (!body || typeof body !== "object") {
    return null;
  }
  const requestId = (body as { requestId?: unknown }).requestId;
  return typeof requestId === "string" && requestId.length > 0
    ? requestId
    : null;
}

export async function fetchCognitoryQuestionAnswerEvaluationSettings(input: {
  cognitoryId: string;
  apiBaseUrl?: string;
  bearerToken?: string | null;
  fetchImpl?: typeof fetch;
}): Promise<CognitoryQuestionAnswerEvaluationSettingsClientResult> {
  const fetchImpl = input.fetchImpl ?? fetch;
  const token =
    input.bearerToken !== undefined
      ? input.bearerToken
      : await resolveCognitoryBearerToken();
  if (!token) {
    return { kind: "unauthenticated", requestId: null };
  }
  const base = input.apiBaseUrl ?? resolveCognitoryApiBaseUrl();
  const path = buildCognitoryQuestionAnswerEvaluationSettingsPath(
    input.cognitoryId,
  );
  let response: Response;
  try {
    response = await fetchImpl(`${base}${path}`, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: "application/json",
      },
      cache: "no-store",
    });
  } catch {
    return { kind: "temporary_failure", requestId: null };
  }
  let body: unknown = null;
  try {
    body = await response.json();
  } catch {
    body = null;
  }
  const requestId = readJsonRequestId(body);
  if (response.status === 401) {
    return { kind: "unauthenticated", requestId };
  }
  if (response.status === 403) {
    return { kind: "forbidden", requestId };
  }
  if (response.status === 400) {
    return { kind: "invalid_request", requestId };
  }
  if (response.status === 503) {
    return { kind: "temporary_failure", requestId };
  }
  if (response.status !== 200) {
    return { kind: "temporary_failure", requestId };
  }
  const rebuilt = rebuildCognitoryQuestionAnswerEvaluationSettingsPayload({
    expectedCognitoryId: input.cognitoryId,
    body,
  });
  if (rebuilt.kind !== "success") {
    return { kind: "malformed_success", requestId: rebuilt.supportReference };
  }
  return {
    kind: "success",
    requestId: rebuilt.supportReference,
    cognitoryId: rebuilt.cognitoryId,
    settings: rebuilt.settings,
  };
}

export async function updateCognitoryQuestionAnswerEvaluationSettings(input: {
  cognitoryId: string;
  enabled: boolean;
  apiBaseUrl?: string;
  bearerToken?: string | null;
  fetchImpl?: typeof fetch;
}): Promise<CognitoryQuestionAnswerEvaluationSettingsClientResult> {
  const fetchImpl = input.fetchImpl ?? fetch;
  const token =
    input.bearerToken !== undefined
      ? input.bearerToken
      : await resolveCognitoryBearerToken();
  if (!token) {
    return { kind: "unauthenticated", requestId: null };
  }
  const base = input.apiBaseUrl ?? resolveCognitoryApiBaseUrl();
  const path = buildCognitoryQuestionAnswerEvaluationSettingsPath(
    input.cognitoryId,
  );
  let response: Response;
  try {
    response = await fetchImpl(`${base}${path}`, {
      method: "PUT",
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ enabled: input.enabled }),
      cache: "no-store",
    });
  } catch {
    return { kind: "temporary_failure", requestId: null };
  }
  let body: unknown = null;
  try {
    body = await response.json();
  } catch {
    body = null;
  }
  const requestId = readJsonRequestId(body);
  if (response.status === 401) {
    return { kind: "unauthenticated", requestId };
  }
  if (response.status === 403) {
    return { kind: "forbidden", requestId };
  }
  if (response.status === 400) {
    return { kind: "invalid_request", requestId };
  }
  if (response.status === 503) {
    return { kind: "temporary_failure", requestId };
  }
  if (response.status !== 200) {
    return { kind: "temporary_failure", requestId };
  }
  const rebuilt = rebuildCognitoryQuestionAnswerEvaluationSettingsPayload({
    expectedCognitoryId: input.cognitoryId,
    body,
  });
  if (rebuilt.kind !== "success") {
    return { kind: "malformed_success", requestId: rebuilt.supportReference };
  }
  return {
    kind: "success",
    requestId: rebuilt.supportReference,
    cognitoryId: rebuilt.cognitoryId,
    settings: rebuilt.settings,
  };
}
